import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { labelColors } from '../../theme';
import { DisagreementCandidate, DisagreementCandidateReview } from '../../types';
import ClassificationChip from '../common/ClassificationChip';

export const disagreementChangeFlashSx = {
  '@keyframes disagreementPanelIn': {
    from: { opacity: 0.4 },
    to: { opacity: 1 },
  },
  animation: 'disagreementPanelIn 450ms ease-out',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

interface DisagreementCandidateSummaryProps {
  candidate: DisagreementCandidate;
  reviewerName: (userId: string) => string;
}

const reviewDate = (review: DisagreementCandidateReview) =>
  new Date(review.reviewedAt).toLocaleDateString();

const DisagreementCandidateSummary: React.FC<DisagreementCandidateSummaryProps> = ({
  candidate,
  reviewerName,
}) => {
  const fromPreviousReview = candidate.groundTruthSource === 'previous_reviewed_output';
  const hasDisagreement = candidate.hasDisagreement !== false;

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.25,
          mb: 1.5,
        }}
      >
        <Paper
          key={`${candidate.transcriptionId}:${candidate.sentence}:gt`}
          variant="outlined"
          data-testid="ground-truth-summary"
          sx={{
            borderLeft: `4px solid ${labelColors[candidate.humanLabel].border}`,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 160,
            minWidth: 0,
            overflow: 'hidden',
            p: 1.5,
            ...disagreementChangeFlashSx,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
              mb: 0.75,
            }}
          >
            <Typography variant="subtitle2">Ground Truth</Typography>
            <ClassificationChip value={candidate.humanLabel} />
            {typeof candidate.fragmentIndex === 'number' && (
              <Chip label={`#${candidate.fragmentIndex}`} size="small" variant="outlined" />
            )}
            <Chip
              label={fromPreviousReview ? 'Previous Disagreement Review' : 'Label consensus'}
              size="small"
              color={fromPreviousReview ? 'warning' : 'default'}
              variant="outlined"
            />
            {!hasDisagreement && (
              <Chip label="Agreement" size="small" color="success" variant="outlined" />
            )}
            {!fromPreviousReview && candidate.humanReviewerCount > 0 && (
              <Tooltip
                title={(candidate.humanReviewerIds ?? []).map(reviewerName).join(', ')}
                disableHoverListener={!candidate.humanReviewerIds?.length}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={candidate.humanReviewerIds?.length ? { cursor: 'help' } : undefined}
                >
                  {candidate.humanReviewerCount} reviewer
                  {candidate.humanReviewerCount === 1 ? '' : 's'}
                </Typography>
              </Tooltip>
            )}
          </Box>

          {fromPreviousReview && candidate.previousReview && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Typography variant="body2">
                {candidate.previousReview.reason || '(no reason)'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Reviewed {reviewDate(candidate.previousReview)}
              </Typography>
            </Box>
          )}

          {!fromPreviousReview && candidate.humanReasons.length === 0 && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Typography variant="body2" color="text.secondary">
                {candidate.humanReviewerCount > 0
                  ? '(no reason given)'
                  : 'No explicit labels recorded for this entity.'}
              </Typography>
            </Box>
          )}

          {!fromPreviousReview && candidate.humanReasons.length > 0 && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {candidate.humanReasons.map((reason, index) => {
                const notCounted = reason.counted === false;
                return (
                  <Box
                    key={`${reason.userId}-${index}`}
                    data-testid={notCounted ? 'not-counted-reason' : undefined}
                    sx={{
                      mt: index === 0 ? 0 : 0.75,
                      display: 'flex',
                      alignItems: 'baseline',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      ...(notCounted && { opacity: 0.6 }),
                    }}
                  >
                    {notCounted && <Chip label="Not counted" size="small" variant="outlined" />}
                    {reason.label && reason.label !== candidate.humanLabel && (
                      <ClassificationChip value={reason.label} />
                    )}
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {reviewerName(reason.userId)}:
                    </Typography>
                    <Typography variant="body2" component="span">
                      {reason.reason || '(no reason)'}
                    </Typography>
                    {notCounted && reason.notCountedReason && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexBasis: '100%' }}
                      >
                        {reason.notCountedReason}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        <Paper
          key={`${candidate.transcriptionId}:${candidate.sentence}:ai`}
          variant="outlined"
          sx={{
            borderLeft: `4px solid ${labelColors[candidate.aiLabel].border}`,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 160,
            minWidth: 0,
            overflow: 'hidden',
            p: 1.5,
            ...disagreementChangeFlashSx,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
              mb: 0.75,
            }}
          >
            <Typography variant="subtitle2">AI Label</Typography>
            <ClassificationChip value={candidate.aiLabel} />
          </Box>
          <Typography variant="body2" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {candidate.aiReason || '(no reason)'}
          </Typography>
        </Paper>
      </Box>

      {candidate.alreadyReviewed && candidate.review && (
        <Box
          sx={{
            p: 1.25,
            mb: 1.5,
            borderRadius: 1,
            bgcolor: 'rgba(25, 118, 210, 0.06)',
            borderLeft: '3px solid #1976d2',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2">Resolution</Typography>
            <ClassificationChip value={candidate.review.correctedClassification} />
            <Chip label="Reviewed" size="small" color="primary" />
            {typeof candidate.review.fragmentIndex === 'number' && (
              <Chip label={`#${candidate.review.fragmentIndex}`} size="small" variant="outlined" />
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, maxHeight: 72, overflow: 'auto' }}>
            {candidate.review.reason || '(no reason)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Reviewed {reviewDate(candidate.review)}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default DisagreementCandidateSummary;
